import { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useIntl } from "react-intl";
import type {
    CampoInventario,
    FilaInventario,
    ValoresFormularioInventario,
} from "../types/catalogoInventario.types";
import {
    CAMPOS_INVENTARIO,
    COLORES_INVENTARIO,
    VALORES_INICIALES_INVENTARIO,
} from "../constants/catalogoInventario.constants";

interface Opciones {
    onCrear: (valores: ValoresFormularioInventario, proveedores: number[]) => Promise<boolean>;
    onEditar: (idInventario: number, valores: ValoresFormularioInventario) => Promise<boolean>;
}

const valoresDesdeFila = (f: FilaInventario): ValoresFormularioInventario => ({
    clave: f.clave,
    nombre: f.nombre,
    precio: String(f.precio),
    cantidad: "",
    id_unidad_medida: f.idUnidadMedida ? String(f.idUnidadMedida) : "",
    id_categoria: f.idCategoria ? String(f.idCategoria) : "",
    id_ubicacion: f.idUbicacion ? String(f.idUbicacion) : "",
    id_tipo_producto: f.idTipoProducto ? String(f.idTipoProducto) : "",
    maximos: String(f.maximos),
    minimos: String(f.minimos),
    punto_reorden: String(f.puntoReorden),
});

export const useFormularioInventario = ({ onCrear, onEditar }: Opciones) => {
    const intl = useIntl();
    const [abierto, setAbierto] = useState(false);
    const [productoEditando, setProductoEditando] = useState<FilaInventario | null>(null);
    const [proveedores, setProveedores] = useState<number[]>([]);

    const esEdicion = productoEditando !== null;

    const camposVisibles = useMemo(
        () => CAMPOS_INVENTARIO.filter((c) => !(esEdicion && c.soloAlCrear)),
        [esEdicion]
    );

    const esquema = useMemo(() => {
        const requerido = intl.formatMessage({ id: "input_validation_requerido" });
        const forma: Partial<Record<CampoInventario, Yup.StringSchema>> = {};
        camposVisibles.forEach((c) => {
            let regla = Yup.string();
            if (c.soloNumeros === "entero") regla = regla.matches(/^\d+$/, requerido);
            if (c.soloNumeros === "decimal") regla = regla.matches(/^\d+(\.\d+)?$/, requerido);
            forma[c.nombre] = c.requerido ? regla.required(requerido) : regla;
        });
        return Yup.object(forma);
    }, [camposVisibles, intl]);

    const formik = useFormik<ValoresFormularioInventario>({
        initialValues: VALORES_INICIALES_INVENTARIO,
        validationSchema: esquema,
        enableReinitialize: false,
        onSubmit: async (valores) => {
            const guardado = productoEditando
                ? await onEditar(productoEditando.id, valores)
                : await onCrear(valores, proveedores);
            if (guardado) cerrar();
        },
    });

    const abrirCrear = () => {
        setProductoEditando(null);
        setProveedores([]);
        formik.resetForm({ values: VALORES_INICIALES_INVENTARIO });
        setAbierto(true);
    };

    const abrirEditar = (fila: FilaInventario) => {
        setProductoEditando(fila);
        setProveedores([]);
        formik.resetForm({ values: valoresDesdeFila(fila) });
        setAbierto(true);
    };

    const cerrar = () => {
        setAbierto(false);
        setProductoEditando(null);
        setProveedores([]);
        formik.resetForm({ values: VALORES_INICIALES_INVENTARIO });
    };

    const colorCampo = (campo: CampoInventario): string | undefined =>
        formik.touched[campo] && !formik.errors[campo] && formik.values[campo] !== ""
            ? COLORES_INVENTARIO.campoValido
            : undefined;

    return {
        formik,
        abierto,
        esEdicion,
        camposVisibles,
        proveedores,
        setProveedores,
        abrirCrear,
        abrirEditar,
        cerrar,
        colorCampo,
        productoEditando
    };
};
